import { useAuthContext } from "../../auth/state/AuthContext";
import Header from "../../shared/app-shell/Header";
import PageFooter from "../../shared/app-shell/PageFooter";
import Sidebar from "../../shared/app-shell/Sidebar";
import { themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { logE, log, logV } from "../../shared/services/debug-logger";
import { getVoyageAssesmentStatus } from "../../voyage-assessment/services/voyage-assesment.service";
import KeyDriver from "../components/AddKeyDriver";
import AddKeyDriverButton from "../components/AddKeyDriverButton";
import ConfirmationMessage from "../components/ConfirmationMessage";
import PnlTable from "../components/PnlTable";
import TableButtons from "../components/TableButtons";
import TopPnlClarifications from "../components/TopPnlClarifications";
import VarianceSection from "../components/VarianceSection";
import BlurredAlert from "../components/WarningMessage";
import "../components/styling/ScrollWrapper.css";
import { RowRef } from "../models/DriverInputRow.model";
import { RouteParams } from "../models/VoyagePnl.model";
import { postVoyagePnlData, useVoyagePnlData } from "../services/pnl.service";
import { ALL_DRIVERS, getDriverDefinition } from "../utils/DriversConfig";
import {
  excludedRows,
  normalizePostData,
  NamingDriverRows,
  combinedPostDriverData,
  getDriversWithDataFromForm,
  filterUnallocatedPerCategory,
  hasDriverDataInLocalStorage,
  removeDriverFromStore,
  getDriversWithDataFromStore,
  hasVoyageDriversStoreSnapshot,
  normalizeToKnownDrivers,
  extractImpactPerCategory,
  computeTotals,
  updateKeyDriversUnallocated,
  computeOverallUnallocated,
  clearVoyageDriversStore,
  resetVoyageState,
  shallowEqualList,
  isRejectedStatus,
  useInitialDriversOpen,
  continueClickedAtomFamily,
  submitEnabledAtomFamily,
  driversSequenceAtomFamily,
  formSubmittedAtomFamily,
  assesmentCompletedAtomFamily,
  showAlertAtomFamily,
  enabledFlagsAtomFamily,
} from "../utils/UtilFunctions";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const VoyagePnlPage: React.FC = () => {
  const theme = useAtomValue(themeAtom);

  const navigate = useNavigate();
  const authContext = useAuthContext();

  // Obtaining the data from the backend / route
  const { tcCode, voyageNo, isApprover } = useParams<RouteParams>();
  const voyageScope = useMemo(() => ({ tcCode, voyageNo }), [tcCode, voyageNo]);

  // ✅ Voyage-scoped flags (pass tcCode & voyageNo so they don't bleed across voyages)
  const [continueClicked, setContinueClicked] = useAtom(continueClickedAtomFamily(voyageScope));
  const [submitEnabled, setSubmitEnabled] = useAtom(submitEnabledAtomFamily(voyageScope));
  const [driversSequence, setDriversSequence] = useAtom(driversSequenceAtomFamily(voyageScope));
  const [formSubmitted, setFormSubmitted] = useAtom(formSubmittedAtomFamily(voyageScope));
  const [assesmentCompleted, setAssesmentCompleted] = useAtom(assesmentCompletedAtomFamily(voyageScope));
  const [showAlert, setShowAlert] = useAtom(showAlertAtomFamily(voyageScope));
  const enabledFlags = useAtomValue(enabledFlagsAtomFamily(voyageScope));
  const [isOwnerRatingSubmitted, setIsOwnerRatingSubmitted] = useState(false);

  const hasCalculatedUnallocated = useRef(false);

  const isApprovalMode = isApprover === "approval";

  const { voyagePnlData, setVoyagePnlData, hasError, setHasError, isLoading, setIsLoading } = useVoyagePnlData(
    tcCode,
    voyageNo,
  );

  const assessmentstatus = voyagePnlData?.assessmentStatus?.assessmentCompleted;
  const rejectionComment = voyagePnlData?.assessmentStatus?.comment ?? "";
  const isRejected = isRejectedStatus(assessmentstatus, rejectionComment);

  const formRef = useRef<HTMLFormElement>(null);
  const unallocTimerRef = useRef<number | null>(null);
  const driverResetRefs = useRef<Record<string, () => void>>({});
  const rowRefs = useRef<(RowRef | null)[]>([]);
  const commentResetRefs = useRef<Record<string, () => void>>({});

  // Approval mode must be backend-driven only; keep voyage local store clean.
  useEffect(() => {
    if (!isApprovalMode) return;
    clearVoyageDriversStore(tcCode, voyageNo);
  }, [isApprovalMode, tcCode, voyageNo]);

  const updateUnallocated = useCallback(() => {
    // Debounce to batch rapid changes (like typing)
    if (unallocTimerRef.current !== null) {
      window.clearTimeout(unallocTimerRef.current);
    }

    unallocTimerRef.current = window.setTimeout(() => {
      const form = formRef.current;
      if (!form) return;

      const formData = new FormData(form);
      const data = Object.fromEntries(formData.entries()) as Record<string, string>;

      setVoyagePnlData((prev) => {
        if (!prev?.keyDrivers || !prev?.pnlParams) return prev;

        // 1) Build the whitelist of categories to include
        const categoriesForUnallocateCalculation = prev.keyDrivers
          .map((item) => item.category)
          .filter((category) => !excludedRows.includes(category));

        // 2) Use your extracted parser
        const impactPerCategory = extractImpactPerCategory(data, categoriesForUnallocateCalculation);

        // 3) ✅ Use your extracted unallocated helpers
        const totals = computeTotals(impactPerCategory);
        const updatedKeyDrivers = updateKeyDriversUnallocated(prev.keyDrivers, impactPerCategory, excludedRows);
        const overallUnallocated = computeOverallUnallocated(prev.pnlParams.variance, totals);

        // 4) Return the new state
        return {
          ...prev,
          keyDrivers: updatedKeyDrivers,
          pnlParams: { ...prev.pnlParams, unallocated: overallUnallocated },
        };
      });
    }, 50);
  }, [setVoyagePnlData]);

  // Optional: clear any pending timer when unmounting
  useEffect(() => {
    return () => {
      if (unallocTimerRef.current !== null) {
        window.clearTimeout(unallocTimerRef.current);
      }
    };
  }, []);

  // Calculates the unallocated upon loading
  useEffect(() => {
    if (!hasCalculatedUnallocated.current && voyagePnlData) {
      updateUnallocated();
      hasCalculatedUnallocated.current = true;
    }
  }, [voyagePnlData, updateUnallocated]);

  //Reset the “first unallocated calc” when voyage changes to allow recalculation
  useLayoutEffect(() => {
    hasCalculatedUnallocated.current = false;
    setFormSubmitted(false);
    setAssesmentCompleted(false);
    setShowAlert(false);
  }, [tcCode, voyageNo, setFormSubmitted, setAssesmentCompleted, setShowAlert]);

  // Approval route should always initialize clean UI state per voyage.
  useEffect(() => {
    if (!isApprovalMode) return;
    setDriversSequence([]);
    setContinueClicked(false);
    setSubmitEnabled(false);
  }, [isApprovalMode, tcCode, voyageNo, setDriversSequence, setContinueClicked, setSubmitEnabled]);

  // Prefetch owner rating status so confirmation action buttons render instantly.
  useEffect(() => {
    let isMounted = true;

    const fetchOwnerRatingStatus = async () => {
      if (!tcCode || !voyageNo) return;

      const cacheKey = `voyageOwnerRatingStatus:${tcCode}:${voyageNo}`;
      const cachedStatus = sessionStorage.getItem(cacheKey);

      if (cachedStatus === "sent" || cachedStatus === "done") {
        setIsOwnerRatingSubmitted(true);
      } else if (cachedStatus) {
        setIsOwnerRatingSubmitted(false);
      }

      const status = await getVoyageAssesmentStatus(tcCode, voyageNo);
      if (!isMounted) return;

      sessionStorage.setItem(cacheKey, status);
      setIsOwnerRatingSubmitted(status === "sent" || status === "done");
    };

    fetchOwnerRatingStatus();

    return () => {
      isMounted = false;
    };
  }, [tcCode, voyageNo]);

  // Checks the condition of the assessment and sets the state
  useEffect(() => {
    setAssesmentCompleted(assessmentstatus === "APPROVED");
    setFormSubmitted(assessmentstatus === "SUBMITTED" && isApprover !== "approval");
  }, [isApprover, assessmentstatus, setAssesmentCompleted, setFormSubmitted]);

  const clearDriverLocalCache = useCallback(
    (driverName: string) => {
      if (!tcCode || !voyageNo) return;
      removeDriverFromStore(tcCode, voyageNo, driverName);
    },
    [tcCode, voyageNo],
  );

  const removeDriverFromContext = useCallback(
    (driverName: string) => {
      setVoyagePnlData((prev) => {
        if (!prev) return prev;
        const nextAddDrivers = (prev.addDrivers ?? []).filter((d) => d.driver !== driverName);
        return { ...prev, addDrivers: nextAddDrivers };
      });
    },
    [setVoyagePnlData],
  );

  // --- NEW: Clear rows + comments for a driver (and recalc unallocated)
  const clearDriver = useCallback(
    (driverName: string, opts?: { skipRowReset?: boolean }) => {
      if (!opts?.skipRowReset) {
        driverResetRefs.current[driverName]?.();
      }

      clearDriverLocalCache(driverName);
      removeDriverFromContext(driverName);
      setDriversSequence((prev) => prev.filter((n) => n !== driverName));

      updateUnallocated();
    },
    [removeDriverFromContext, updateUnallocated, clearDriverLocalCache, setDriversSequence],
  );

  // Make driversSequence the single source of truth: sync with "used" list
  const syncSequenceToUsed = useCallback(
    (usedDrivers: string[], includeCommentsInGuard: boolean, opts?: { allowMutations?: boolean }) => {
      const allowMutations = opts?.allowMutations ?? true;
      if (allowMutations) {
        ALL_DRIVERS.forEach((name) => {
          if (!usedDrivers.includes(name)) {
            const hasData = hasDriverDataInLocalStorage(tcCode, voyageNo, name, {
              includeComments: includeCommentsInGuard,
            });
            if (!hasData) clearDriver(name, { skipRowReset: true });
          }
        });
      }

      setDriversSequence((prev) => (shallowEqualList(prev, usedDrivers) ? prev : usedDrivers));
    },
    [tcCode, voyageNo, clearDriver, setDriversSequence],
  );

  useInitialDriversOpen({
    tcCode,
    voyageNo,
    isApprovalMode,
    allDrivers: ALL_DRIVERS,
    addDrivers: voyagePnlData?.addDrivers,
    hasStoreSnapshot: hasVoyageDriversStoreSnapshot,
    getDriversFromStore: getDriversWithDataFromStore,
    syncSequenceToUsed,
  });

  // In approval mode, source of truth for opened drivers is backend payload only.
  useEffect(() => {
    if (!isApprovalMode) return;
    if (isLoading) return;
    if (!voyagePnlData) return;

    const backendDrivers = normalizeToKnownDrivers(
      (voyagePnlData.addDrivers ?? []).map((d) => d.driver),
      ALL_DRIVERS,
    );

    setDriversSequence((prev) => (shallowEqualList(prev, backendDrivers) ? prev : backendDrivers));
  }, [isApprovalMode, isLoading, voyagePnlData, setDriversSequence]);

  // In approval mode, UI must reflect backend data only (no local hydration).

  // Opens the used drivers upon loading the page if any
  const enhancedDrivers = useMemo(
    () =>
      ALL_DRIVERS.map((name) => {
        const isOn = driversSequence.includes(name);
        return {
          name,
          definition: getDriverDefinition(name),
          isOn,
          toggle: () => {
            if (isApprovalMode) return;
            if (isOn) {
              clearDriver(name);
            } else {
              setDriversSequence((prev) => {
                const next = prev.filter((n) => n !== name);
                next.push(name);
                return next;
              });
              updateUnallocated();
            }
          },
        };
      }),
    [driversSequence, clearDriver, setDriversSequence, updateUnallocated, isApprovalMode],
  );

  const actualNeededDrivers = useMemo(() => {
    const byName = new Map(enhancedDrivers.map((d) => [d.name, d]));
    return driversSequence
      .map((name) => byName.get(name))
      .filter((d): d is (typeof enhancedDrivers)[number] => Boolean(d));
  }, [enhancedDrivers, driversSequence]);

  // When Cancel and Exit is clicked this function resets all drivers
  const handleDriversReset = useCallback(() => {
    resetVoyageState({
      tcCode,
      voyageNo,
      rowRefs,
      commentResetRefs,
      formRef,
      setVoyagePnlData,
      setDriversSequence,
      unallocTimerRef,
      after: updateUnallocated,
    });
  }, [
    tcCode,
    voyageNo,
    rowRefs,
    commentResetRefs,
    formRef,
    setVoyagePnlData,
    setDriversSequence,
    unallocTimerRef,
    updateUnallocated,
  ]);

  // When Continue is Clicked this function handles the behavior including submit functionality
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    log("VoyagePnlPage - handleSubmit", { continueClicked, submitEnabled });
    let transformedPostData = {};
    let submitSucceeded = false;

    if (continueClicked && formRef.current) {
      try {
        const formData = new FormData(formRef.current);
        const data = Object.fromEntries(formData.entries()) as Record<string, string>;

        const driversInUseRaw = getDriversWithDataFromForm(
          data,
          driversSequence.map((name) => ({ name })),
          { includeComments: false },
        );

        const driversInUse = normalizeToKnownDrivers(driversInUseRaw, ALL_DRIVERS);

        syncSequenceToUsed(driversInUse, false);

        if (driversInUse.length === 0) {
          log("VoyagePnlPage - handleSubmit - no drivers");
          setVoyagePnlData((prev) => (prev ? { ...prev, addDrivers: [] } : prev));
          setDriversSequence([]);
          setContinueClicked(false);
          return;
        }

        const submittedBy = authContext?.state.user?.accessToken.sub || "Unknown user";
        const extractedComments = Object.fromEntries(
          Object.entries(data)
            .filter(([key]) => key.endsWith("Comments"))
            .map(([key, value]) => {
              const cleanedKey = key.replace(/^voyagePnl:[^:]+:[^:]+:comment:/, "");
              return [cleanedKey, value];
            }),
        );
        const extractedQuantitiesOptionsRatesAndImpacts = normalizePostData(data);
        const extractedUnallocated = filterUnallocatedPerCategory(voyagePnlData?.keyDrivers);
        const imoNumber = voyagePnlData?.voyageParams?.imoNumber ?? "Unknown imoNumber";
        const vesselName = voyagePnlData?.voyageParams?.vesselName ?? "Unknown vesselName";

        if (tcCode && voyageNo) {
          const voyageNumber = Number(voyageNo);

          transformedPostData = combinedPostDriverData(
            tcCode,
            voyageNumber,
            imoNumber,
            vesselName,
            submittedBy,
            extractedUnallocated,
            extractedComments,
            extractedQuantitiesOptionsRatesAndImpacts,
          );
        }

        if (submitEnabled && driversInUse.length > 0) {
          setIsLoading(true);

          const postResult = await postVoyagePnlData(transformedPostData);
          if (postResult === null) {
            log("VoyagePnlPage - handleSubmit - no response");
            setShowAlert(true);
            return;
          }
          submitSucceeded = true;

          try {
            resetVoyageState({
              tcCode,
              voyageNo,
              rowRefs,
              commentResetRefs,
              formRef,
              setVoyagePnlData,
              setDriversSequence,
              unallocTimerRef,
              after: updateUnallocated,
            });
          } catch (cleanupError) {
            logE(new Error("VoyagePnlPage - handleSubmit - cleanup failed"));
          }

          setShowAlert(false);
          setFormSubmitted(true);
          log("STATE - handleSubmit - submitted");

          try {
            setContinueClicked(false);
            setSubmitEnabled(false);
          } catch (uiStateError) {
            logE(new Error("VoyagePnlPage - handleSubmit - persisted state cleanup failed"));
          }
        }
      } catch (err) {
        logE(new Error("VoyagePnlPage - handleSubmit - failed"));
        if (!submitSucceeded) {
          setShowAlert(true);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  logV("VoyagePnlPage - BUILD", { loading: isLoading, approval: isApprovalMode });

  return (
    <>
      <div className="flex h-screen w-full min-w-[1250px] max-w-full">
        <Sidebar />
        <div
          className={twMerge(
            "flex flex-1 flex-col gap-8 overflow-y-auto px-6",
            theme.home.tailwind.pageBackground,
            theme.home.tailwind.textDefault,
          )}
        >
          <Header />
          <div className="flex flex-col gap-6">
            <TopPnlClarifications />
            {formSubmitted || assesmentCompleted ? (
              <ConfirmationMessage
                linkTo="/"
                assesmentCompleted={assesmentCompleted}
                isOwnerRatingSubmitted={isOwnerRatingSubmitted}
                resetDrivers={handleDriversReset}
              />
            ) : (
              <form ref={formRef} onSubmit={handleSubmit} onInput={updateUnallocated}>
                <div
                  className={twMerge(
                    "flex w-full flex-col gap-2 rounded-lg border px-10 py-6",
                    theme.home.tailwind.panelBackground,
                    theme.home.tailwind.textDefault,
                    theme.uwhcAssistant.border,
                  )}
                >
                  <VarianceSection />

                  {/* The Class contolls the external table */}
                  <div
                    className={twMerge(
                      "relative mb-6 flex flex-col overflow-hidden rounded-xl border",
                      theme.uwhcAssistant.border,
                      !continueClicked
                        ? enabledFlags > 0 && enabledFlags < 3
                          ? "w-max"
                          : "w-full"
                        : enabledFlags < 4
                          ? "w-max"
                          : "w-full",
                    )}
                  >
                    {/* ✅ Use enhancedDrivers so a hide also clears rows+comments */}
                    {!continueClicked && !isApprovalMode && <AddKeyDriverButton drivers={enhancedDrivers} />}
                    <div className="flex w-full flex-row items-start p-0">
                      <div
                        className={twMerge(
                          "w-full min-w-[650px]",
                          enabledFlags >= 1 ? twMerge("max-w-[650px] border-r", theme.uwhcAssistant.border) : "",
                          continueClicked ? "max-w-[650px]" : "",
                        )}
                      >
                        <PnlTable />
                      </div>

                      <div className={`scroll-wrapper flex ${!continueClicked ? "" : enabledFlags > 0 ? "w-max" : ""}`}>
                        {/* Build rowIndexOffset and driverRanges consistently during render */}
                        {(() => {
                          let rowIndexOffset = 0;

                          return actualNeededDrivers.map(({ name, definition, toggle }) => {
                            const namingDriverRows = NamingDriverRows(
                              name,
                              voyagePnlData?.keyDrivers ?? [],
                              excludedRows,
                            );

                            const currentOffset = rowIndexOffset;
                            rowIndexOffset += namingDriverRows.length;

                            return (
                              <KeyDriver
                                key={name}
                                driverName={name}
                                driverDefinition={definition}
                                rowRefs={rowRefs}
                                rowOffset={currentOffset}
                                commentResetRefs={commentResetRefs}
                                onClose={toggle}
                                registerReset={(driverName, fn) => {
                                  driverResetRefs.current[driverName] = fn;
                                  return () => {
                                    delete driverResetRefs.current[driverName];
                                  };
                                }}
                              />
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>

                  <div className="mt-1 flex w-full items-end justify-between gap-3 max-sm:flex-col max-sm:items-stretch">
                    {isRejected && (
                      <div className="w-[80%] min-w-[320px] max-w-[910px]">
                        <div className="grid grid-cols-[auto,1fr] items-center gap-x-2">
                          <label
                            htmlFor="rejectionComment"
                            className={twMerge("text-sm font-medium", theme.home.tailwind.textDefault)}
                          >
                            Approver Comment
                          </label>

                          <textarea
                            id="rejectionComment"
                            readOnly
                            value={rejectionComment}
                            rows={3}
                            className={twMerge(
                              "h-15 w-full resize-none rounded border px-2 py-1 text-sm leading-6 placeholder:text-gray-400 focus:outline-none focus:ring-2",
                              theme.uwhcAssistant.inputBorder,
                              theme.uwhcAssistant.theadBg,
                              theme.home.tailwind.textDefault,
                              theme.headerComponent.tailwind.buttonFocusRing,
                            )}
                            placeholder="No comment provided"
                          />
                        </div>
                      </div>
                    )}

                    <div className="shrink-0">
                      <TableButtons onReset={handleDriversReset} />
                    </div>
                  </div>
                </div>
              </form>
            )}
            <PageFooter />
          </div>
        </div>
      </div>
      {/* Loading indicator in case loading takes longer time */}
      {isLoading && (
        <LoadingWrapper
          isLoading={isLoading}
          loadingIndicatorClassName="text-green"
          loadingIndicatorSize="lg"
          zIndex={60}
        />
      )}

      {/* Warning Message in case loading of PnL data failed - get method */}
      {hasError && (
        <BlurredAlert
          message="Failed to load data, please contact support!"
          onClose={() => {
            setHasError(false);
            navigate("/");
          }}
        />
      )}

      {/* Warning Message in case submitting of data failed - post method */}
      {showAlert && (
        <BlurredAlert
          message="Submitting of data failed, please contact support!"
          onClose={() => setShowAlert(false)}
        />
      )}
    </>
  );
};

export default VoyagePnlPage;
